import type { QueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";
import { rememberCampaignReturn } from "../components/legacy/app-state.tsx";
import { Header, ShellProvider } from "../components/legacy/Header.tsx";
import { ToastProvider } from "../components/legacy/ui.tsx";
import { NotificationsHost } from "../components/notifications/NotificationsHost.tsx";
import { type AppConfig, api } from "../lib/api.ts";

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

/** Single bootstrap request: the session. */
export function useAppConfig() {
  return useQuery({
    queryKey: ["config"],
    queryFn: () => api.get<AppConfig>("/config"),
    staleTime: 5 * 60_000,
  });
}

function RootLayout() {
  const { data, isLoading } = useAppConfig();
  const user = data?.user ?? null;
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const href = useRouterState({ select: (s) => s.location.href });
  useEffect(() => rememberCampaignReturn(href), [href]);

  // The design tool's print target: Browser Run prints this page as the PDF, so no app chrome.
  if (pathname.startsWith("/render/")) return <Outlet />;

  if (isLoading) {
    return <div className="boot">Loading…</div>;
  }

  return (
    <ShellProvider>
      <ToastProvider>
        <div>
          <Header user={user} />
          <NotificationsHost />
          <main>
            <Outlet />
          </main>
        </div>
      </ToastProvider>
    </ShellProvider>
  );
}
